// Schema v3 round storage: one Firestore document PER ROUND instead of one fat
// document holding every round of a group.
//
// Why: in v2 a group's rounds all lived in tournaments/{id}/content/rounds_{g}.
// Entering a single result rewrote that whole document — and pushed all of it to
// every spectator listening. By round 9 of a 60-player event that is ~55 KB per
// keystroke-save, per viewer, which made Firestore EGRESS (not reads) the
// dominant projected cost of running a live public tournament. It also walked
// the document toward Firestore's hard 1 MB cap on large events.
//
// v3 layout, per group {g}:
//   content/rounds_{g}        — INDEX: { split: true, roundNos: [...], structureRev }
//   content/rounds_{g}_r{n}   — { round: {...} }   one per round
//
// The index keeps `structureRev` (the multi-arbiter structure-ownership counter)
// in one authoritative place and states which rounds exist, so a stale or
// orphaned round document is simply never read. Note that structureRev is
// DELIBERATELY not stored on the per-round documents: a structural bump would
// then change every round's payload and rewrite them all, undoing the whole
// point of the split.
//
// The v2 fat document is still read transparently (readGroupRounds), and the
// first v3 save of a group replaces it in place with the index form.
//
// Pure module: no DOM, no Firebase — the storage layer in
// js/services/firebase-auth.js supplies the I/O.

export function roundsIndexDocId(groupKey) {
    return 'rounds_' + groupKey;
}

export function roundDocId(groupKey, roundNo) {
    return 'rounds_' + groupKey + '_r' + roundNo;
}

// A round's number, falling back to its position for older/imported data that
// never carried one (mergeRounds keys off roundNo, so it must be stable).
export function roundNoOf(round, idx) {
    const n = Number(round && round.roundNo);
    return Number.isFinite(n) && n > 0 ? n : idx + 1;
}

// Build every document for one group's rounds: the per-round docs plus the index.
// Returns { docs: { docId: payload }, roundNos }.
export function buildRoundDocs(groupKey, rounds, structureRev) {
    const list = Array.isArray(rounds) ? rounds : [];
    const docs = {};
    const roundNos = [];
    const used = new Set();

    list.forEach((round, idx) => {
        let no = roundNoOf(round, idx);
        // Two rounds claiming the same number would collapse into one document and
        // silently lose a round. Corrupt/imported data must degrade, not vanish.
        while (used.has(no)) no++;
        used.add(no);
        roundNos.push(no);
        // Stamp the resolved number so the document is self-describing.
        docs[roundDocId(groupKey, no)] = { round: { ...round, roundNo: no } };
    });

    docs[roundsIndexDocId(groupKey)] = {
        split: true,
        roundNos,
        structureRev: structureRev || 0
    };
    return { docs, roundNos };
}

// Read one group's rounds out of a content-doc map, transparently handling both
// the v3 split layout and the legacy v2 fat document.
export function readGroupRounds(contentMap, groupKey) {
    const map = contentMap || {};
    const index = map[roundsIndexDocId(groupKey)] || {};
    const structureRev = index.structureRev || 0;

    if (index.split) {
        const nos = Array.isArray(index.roundNos) ? index.roundNos : [];
        // Follow the index, not the document set: an orphan left behind by a
        // failed delete must not resurrect a round the arbiter undid.
        const rounds = nos
            .map(no => (map[roundDocId(groupKey, no)] || {}).round)
            .filter(r => r && typeof r === 'object');
        return { rounds, structureRev, split: true };
    }

    return {
        rounds: Array.isArray(index.rounds) ? index.rounds : [],
        structureRev,
        split: false
    };
}

// Decide which rounds a save should commit and which stale round documents to
// remove. Mirrors mergeRounds' contract exactly: the higher structureRev owns
// the STRUCTURE (which rounds exist), local wins ties, and per-board results are
// merged separately and independently of who owns the structure.
//
// `hasRemote` distinguishes "the cloud has no index yet" (a brand-new
// tournament) from "the cloud says this group has no rounds".
export function planRoundsWrite({ localNos, localRev, remoteNos, remoteRev, hasRemote }) {
    const local = Array.isArray(localNos) ? localNos.slice() : [];
    const remote = Array.isArray(remoteNos) ? remoteNos.slice() : [];
    const lRev = localRev || 0;
    const rRev = remoteRev || 0;

    // SAFETY INVARIANT (same one the players docs have): a client that hasn't
    // loaded this tournament — a co-arbiter who opened it before its content
    // arrived, or whose fetch failed — must never be able to blank out
    // everyone's rounds. Skip the group's rounds write entirely.
    if (hasRemote && local.length === 0 && remote.length > 0) {
        return { finalNos: remote, toDelete: [], skip: true, remoteOwns: true };
    }

    const remoteOwns = hasRemote && rRev > lRev;
    const finalNos = remoteOwns ? remote : local;
    const keep = new Set(finalNos);
    // Sweep both sides: a round dropped locally (undo round) and one dropped by
    // the winning remote structure both leave a document behind.
    const toDelete = [];
    remote.concat(local).forEach(no => {
        if (!keep.has(no) && toDelete.indexOf(no) === -1) toDelete.push(no);
    });

    return { finalNos, toDelete, skip: false, remoteOwns };
}
